import { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Field,
  Form,
  InputStatuses,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  custom,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { ReferenceIdField, TextField } from '@/components/common';
import { useGetIsccCertificateQuery } from '@/lib/api/lookupsApi';
import { getTranslation } from '@/lib/utils/i18n';
import { useDebounce, useTranslations } from '@/hooks';
import { SITE_FIELDS, siteFieldName, siteNamePrefix } from '../constants/customers.constants';
import { customerSiteValidation } from '../customersValidation';
import { isSiteIdUnique } from '../utils/customersUtils';
import styles from './ShippingSiteCard.module.scss';

/**
 * Props:
 *   siteKey  addresses this card's fields inside the flat form values
 */
const SiteCertificateNumberField = ({ siteKey }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const numberName = siteFieldName(siteKey, SITE_FIELDS.isccCertificateNumber);
  const typed = (values[numberName] ?? '').trim();
  const certificateNumber = useDebounce(typed);
  const { data, isFetching, isError } = useGetIsccCertificateQuery(certificateNumber, {
    skip: !certificateNumber,
  });

  useEffect(() => {
    if (!certificateNumber) {
      setValue(siteFieldName(siteKey, SITE_FIELDS.isccVerified), false);
      setValue(siteFieldName(siteKey, SITE_FIELDS.validFrom), '');
      setValue(siteFieldName(siteKey, SITE_FIELDS.validTill), '');
      return;
    }
    if (isError) {
      setValue(siteFieldName(siteKey, SITE_FIELDS.isccVerified), false);
      return;
    }
    if (!data) return;
    setValue(siteFieldName(siteKey, SITE_FIELDS.isccVerified), true);
    setValue(siteFieldName(siteKey, SITE_FIELDS.validFrom), data.validFrom);
    setValue(siteFieldName(siteKey, SITE_FIELDS.validTill), data.validTo);
  }, [certificateNumber, data, isError, siteKey, setValue]);

  const lookupMessage = useMemo(() => {
    if (isFetching) return t('SITES_CERTIFICATE_LOOKUP_CHECKING');
    if (isError) return t('SITES_CERTIFICATE_LOOKUP_NOT_FOUND');
    return undefined;
  }, [isFetching, isError, t]);

  const lookupStatus = isError ? InputStatuses.error : InputStatuses.default;

  return (
    <Field name={numberName}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FIELD_ISCC_CERTIFICATE_NUMBER')}
          placeholder={t('FORM_ISCC_CERTIFICATE_NUMBER_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={message ? status : lookupStatus}
          message={message ?? lookupMessage}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   siteKey      addresses this card's fields inside the flat form values
 *   heading      the card title, e.g. "Shipping site - 1"
 *   canRemove    false on the last card — the form keeps at least one site
 *   onRemove     (siteKey) => void
 *   existingIds  the customer's saved site ids this card must not reuse
 *   isEdit       locks the site ID — a saved site keeps it
 */
export const ShippingSiteCard = ({
  siteKey,
  heading,
  canRemove,
  onRemove,
  existingIds,
  isEdit = false,
}) => {
  const t = useTranslations();

  const handleRemove = useCallback(() => onRemove(siteKey), [onRemove, siteKey]);
  const field = useCallback((name) => siteFieldName(siteKey, name), [siteKey]);

  const idRules = useMemo(
    () => [
      ...customerSiteValidation.idNumber,
      custom(
        (value, values) => isSiteIdUnique(values, siteKey, existingIds),
        getTranslation('VALIDATION_SITE_ID_UNIQUE'),
      ),
    ],
    [siteKey, existingIds],
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.secondary}
        >
          {heading}
        </Typography>

        {canRemove ? (
          <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleRemove}>
            {t('COMMON_REMOVE')}
          </Button>
        ) : null}
      </div>

      <div className={styles.fields}>
        <Form.Row columns={4}>
          <TextField
            required
            name={field(SITE_FIELDS.shippingTo)}
            label={t('FORM_SHIPPING_TO')}
            placeholder={t('FORM_SHIPPING_LOCATION_PLACEHOLDER')}
          />
          <ReferenceIdField
            label={t('FIELD_SHIPPING_SITE_ID')}
            prefixLabel={t('CUSTOMER_SITES_FORM_ID_PREFIX')}
            numberLabel={t('CUSTOMER_SITES_FORM_ID_NUMBER')}
            rules={idRules}
            readOnly={isEdit}
            namePrefix={siteNamePrefix(siteKey)}
          />
          <TextField
            name={field(SITE_FIELDS.managerName)}
            label={t('FORM_SITE_MANAGER_NAME_OPTIONAL')}
            placeholder={t('FORM_SITE_MANAGER_NAME_PLACEHOLDER')}
          />
          <TextField
            name={field(SITE_FIELDS.managerEmail)}
            label={t('FORM_SITE_MANAGER_EMAIL_OPTIONAL')}
            placeholder={t('FORM_SITE_MANAGER_EMAIL_PLACEHOLDER')}
            rules={customerSiteValidation.email}
          />
        </Form.Row>

        {/* Quarter-width, matching Shipping to above — not a half-width row. */}
        <Form.Row columns={4}>
          <SiteCertificateNumberField siteKey={siteKey} />
          <TextField
            disabled
            type="date"
            name={field(SITE_FIELDS.validTill)}
            label={t('FIELD_VALID_TILL')}
            placeholder={t('FORM_VALID_TILL_PLACEHOLDER')}
          />
        </Form.Row>

        <div className={styles.divider}>
          <Typography
            variant={TypographyVariants.label}
            weight={TypographyWeights.semibold}
            color={TypographyColors.secondary}
          >
            {t('SECTION_SHIPPING_ADDRESS')}
          </Typography>
        </div>

        <Form.Row columns={2}>
          <TextField
            name={field(SITE_FIELDS.addressLine1)}
            label={t('FORM_ADDRESS_LINE_1_OPTIONAL')}
            placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
          />
          <TextField
            name={field(SITE_FIELDS.addressLine2)}
            label={t('FORM_ADDRESS_LINE_2')}
            placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
          />
        </Form.Row>

        {/* Quarter-width per field, same grid as the rows above — not an even 3-way split. */}
        <Form.Row columns={4}>
          <TextField
            name={field(SITE_FIELDS.city)}
            label={t('FORM_CITY_NAME_OPTIONAL')}
            placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
          />
          <TextField
            name={field(SITE_FIELDS.country)}
            label={t('FORM_COUNTRY_NAME_OPTIONAL')}
            placeholder={t('FORM_COUNTRY_NAME_PLACEHOLDER')}
          />
          <TextField
            name={field(SITE_FIELDS.zipCode)}
            label={t('FORM_ZIP_CODE_OPTIONAL')}
            placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
          />
        </Form.Row>
      </div>
    </div>
  );
};
